import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, take, tap } from 'rxjs';

import { Airport } from '@app/modules/core/interfaces/airport';
import { AppFacade } from '@app/app.facade';
import { SearchType } from '@search-bar/entities/search-type';
import { HomeItemDetailWidgetType } from '@home-item-detail/entities/home-item-detail';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { SwipeType } from '@ux/entities/swipe-types.enum';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { HeaderProductType } from '@header/entities/ds-header-config';
import { SearchBarBehavior } from '@shared/base-search-bar/entities/search-bar-behavior';

@Component({
  selector: 'app-home-item-links',
  templateUrl: './home-item-links.component.html',
  styleUrls: ['./home-item-links.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HomeItemLinksComponent {

  @Input() type: HomeItemDetailWidgetType;
  @Input() airlineCode: string;
  @Input() destinationCode: string;
  @Input() source: string;
  @Input() isMobile: boolean;
  @Input() landingDestination: boolean;
  @Input() isLandingDestination: boolean;

  @Output() autosSearch: EventEmitter<void> = new EventEmitter<void>();

  public readonly searchTypes = SearchType;

  public readonly swipeStyle: SwipeType = SwipeType.FULL;

  public readonly icons = Icon;

  public readonly headerProductType = HeaderProductType;

  public readonly searchBarBehavior = SearchBarBehavior.OnLandingWidget;

  public readonly homeItemDetailWidgetType = HomeItemDetailWidgetType;

  private calendarRoute: string = 'calendar';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly appFacade: AppFacade,
    private readonly baseSearchBarFacade: BaseSearchBarFacade,
    private readonly domManipulateService: DomManipulateService
  ) { }

  get airports$(): Observable<Airport[]> {
    return this.appFacade.airports$;
  }

  get searchAirType(): SearchType {
    return this.type === HomeItemDetailWidgetType.Bus ? SearchType.BUSES_RESULTS : SearchType.FLIGHT_RESULTS;
  }

  get isDiffPlace$(): Observable<boolean> {
    return this.baseSearchBarFacade.autoDiffPlace$;
  }

  public editSearchIsActivated$(productType: HeaderProductType): Observable<boolean> {
    return this.baseSearchBarFacade.editSearchIsActivated$(productType);
  }

  public setEditSearchByProduct(productType: HeaderProductType): void {
    this.baseSearchBarFacade.editSearchIsActivated$(productType).pipe(
      take(1),
      tap((editSearchIsActivated) => {
        const current = editSearchIsActivated ? productType : null;
        this.domManipulateService.setBodyScroll(!editSearchIsActivated);
        this.baseSearchBarFacade.setEditSearchByProduct(current, productType);
      })
    ).subscribe().unsubscribe();
  }

  public showMobileCalendar(type: CalendarField, search: SearchType): void {
    if (this.isMobile) {
      const translations = search === SearchType.FLIGHT_RESULTS ? {
        departurePlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.FLIGHT_DEPARTURE',
        returnPlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.FLIGHT_RETURN'
      } : search === SearchType.HOTELS_RESULTS ? {
        departurePlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.DEPART',
        returnPlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.RETURN'
      } : {};
      this.router.navigate([
        { outlets: { search: [this.calendarRoute] } }], {
        state: {
          ...translations,
          activeType: type,
          searchType: search
        },
        relativeTo: this.route, skipLocationChange: true
      });
    }
  }

  public redirectToSearch(): void {
    this.autosSearch.emit();
  }
}
